import { useId } from 'react';
import { Button, Text, makeStyles } from '@fluentui/react-components';
import { agencyCalendarDate, formatRecordedCalendarDate, foundUsThroughLabel, personDisplayName, type Person } from '@vigeo/shared';
import { KeyValueList, tokens, type KeyValueItem } from '@vigeo/ui';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, minWidth: 0 },
  card: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingHorizontalL,
  },
  action: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: tokens.spacingHorizontalL },
  actionText: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  eyebrow: { color: tokens.colorBrandForeground1, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold },
  title: { margin: 0, fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500, fontWeight: tokens.fontWeightSemibold },
  h: { margin: 0, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold },
});

/**
 * The Overview for someone still at the Candidate stage (#428, frame 944:13427).
 *
 * Deliberately small: the one next HR action, then what we already know about them. The
 * readiness panels stay on the Overview for Onboarding and later, where they mean something; a
 * brand-new candidate is "not ready" by definition, and saying so at length was the noise.
 *
 * `hasOffer` comes from the HR workflow records, read by the page. This component draws; it
 * does not decide.
 */
export function CandidateOverview({ person, hasOffer, canEdit, onDraftOffer, onOpenOffer }: {
  readonly person: Person;
  readonly hasOffer: boolean;
  readonly canEdit: boolean;
  readonly onDraftOffer: () => void;
  readonly onOpenOffer: () => void;
}) {
  const styles = useStyles();
  const actionId = useId();
  const aboutId = useId();
  const firstName = person.name.preferredName || person.name.givenName || personDisplayName(person.name);
  const intake = person.intake;
  const source = intake === undefined ? null : foundUsThroughLabel(intake);
  const about: KeyValueItem[] = [
    { label: 'Email', value: person.contact.email },
    { label: 'Phone', value: person.contact.phone ?? 'Not given' },
    ...(source === null ? [] : [{ label: 'Found us through', value: source }]),
    ...(intake?.interviewedOn ? [{ label: 'Interviewed on', value: formatRecordedCalendarDate(intake.interviewedOn) }] : []),
    ...(intake && intake.interviewers.length > 0 ? [{ label: 'Interviewed by', value: intake.interviewers.map((p) => p.name).join(', ') }] : []),
  ];
  const added = formatRecordedCalendarDate(agencyCalendarDate(person.createdAt));

  return (
    <div className={styles.page}>
      <section className={styles.card} aria-labelledby={actionId}>
        <div className={styles.action}>
          <div className={styles.actionText}>
            <span className={styles.eyebrow}>Next HR action</span>
            <h3 id={actionId} className={styles.title}>{hasOffer ? 'Offer in progress' : 'No offer yet'}</h3>
            <Text>Added {added}.</Text>
          </div>
          {hasOffer
            ? <Button size="large" onClick={onOpenOffer}>Open Offer and stage</Button>
            : canEdit ? <Button appearance="primary" size="large" onClick={onDraftOffer}>Draft the offer</Button> : null}
        </div>
      </section>
      <section className={styles.card} aria-labelledby={aboutId}>
        <h3 id={aboutId} className={styles.h}>About {firstName}</h3>
        <KeyValueList items={about} aria-label={`About ${firstName}`} />
      </section>
    </div>
  );
}
